/**
 * The draft: who's on the clock, the board, and your picks. Picks are made on Showdown as you (the same rules and
 * timer as its draft room), and everyone watching sees them straight away.
 */
import { checkPick, getAvailable, getBudgetLeft, getPicker, getPoints, type DraftLeague, type DraftPlayer } from '@ogreclan/league-core';
import type { PokemonData } from '@ogreclan/pokemon-core';
import { useMemo, useState } from 'preact/hooks';
import type { LeagueProps } from '../pages/league';
import { Mon, nameOf, speciesOf, Types } from '../pokemon';
import { ActionButton, Countdown } from '../ui';
import { boardSlots, you as findYou } from './common';

function Board({ league, data, youId }: { league: DraftLeague, data: PokemonData | null, youId: string | null }) {
	const slots = useMemo(() => boardSlots(league), [league]);
	const count = league.players.length;
	if (!count) return <p class="empty">No players yet.</p>;
	const rounds = Math.ceil(slots.length / count);
	const cell = (round: number, i: number) => {
		const index = league.settings.snake && round % 2 === 1 ? count - 1 - i : i;
		return slots[round * count + index];
	};
	return <div class="table-wrap"><table class="board">
		<thead><tr><th>Round</th>{league.players.map(p => <th key={p.id}>{p.id === youId ? <strong>{p.name}</strong> : p.name}</th>)}</tr></thead>
		<tbody>{Array.from({ length: rounds }, (_, round) => <tr key={round}>
			<td class="muted">{round + 1}</td>
			{league.players.map((p, i) => {
				const slot = cell(round, i);
				if (!slot) return <td key={p.id} />;
				const isNext = league.phase === 'draft' && slot.n === league.pickNumber;
				return <td key={p.id} class={`${slot.skipped && !slot.late ? 'skipped' : ''} ${isNext ? 'next' : ''}`} title={`Pick ${slot.n + 1}`}>
					{slot.species ? <Mon data={data} id={slot.species} /> : slot.late ? <><Mon data={data} id={slot.late} /> <span class="muted small">(late)</span></> :
						slot.skipped ? 'Skipped' : isNext ? <span class="muted">On the clock</span> : ''}
				</td>;
			})}
		</tr>)}</tbody>
	</table></div>;
}

/** Your pick: every Pokémon still available, which you can afford, and a button to draft it */
function PickPanel({ league, data, me, act }: { league: DraftLeague, data: PokemonData | null, me: DraftPlayer, act: LeagueProps['act'] }) {
	const [search, setSearch] = useState('');
	const [onlyAffordable, setOnlyAffordable] = useState(true);
	const [confirming, setConfirming] = useState('');
	const onTheClock = getPicker(league)?.id === me.id;
	const available = useMemo(() => getAvailable(league).map(id => {
		let problem = '';
		try {
			checkPick(league, me, id);
		} catch (e) {
			problem = (e as Error).message;
		}
		return { id, name: nameOf(data, id), points: getPoints(league, id), types: speciesOf(data, id)?.types || [], problem };
	}).sort((a, b) => b.points - a.points || a.name.localeCompare(b.name)), [league, data, me]);
	const query = search.trim().toLowerCase();
	const shown = available.filter(mon => (!onlyAffordable || !mon.problem) &&
		(!query || mon.name.toLowerCase().includes(query) || mon.types.some(type => type.toLowerCase() === query)));

	return <div class="card">
		<h2>{onTheClock ? `It's your pick` : `Make up a skipped pick`}</h2>
		<p>{league.settings.type === 'points' && <>{getBudgetLeft(league, me)} points left · </>}
			{league.settings.picks - me.roster.length} Pokémon to go{me.owedPicks > 0 && <> · you're owed {me.owedPicks} pick{me.owedPicks === 1 ? '' : 's'}</>}</p>
		<div class="row" style="margin-bottom:8px">
			<input type="search" placeholder="Search by name or type" value={search} onInput={e => setSearch((e.target as HTMLInputElement).value)} style="flex:1;min-width:200px" />
			<label class="row small"><input type="checkbox" checked={onlyAffordable} onChange={e => setOnlyAffordable((e.target as HTMLInputElement).checked)} />
				Only ones I can pick</label>
		</div>
		<div class="picker-list"><table><tbody>
			{shown.map(mon => <tr key={mon.id} class={mon.problem ? 'cant' : ''} title={mon.problem}>
				<td><Mon data={data} id={mon.id} /></td>
				<td><Types types={mon.types} /></td>
				{league.settings.type === 'points' && <td class="num">{mon.points}</td>}
				<td class="right nowrap">{confirming === mon.id ? <>
					<ActionButton class="primary small" action={async () => {
						if (await act('pick', mon.id, `You drafted ${mon.name}.`)) setConfirming('');
					}}>Draft {mon.name}{league.settings.type === 'points' ? ` for ${mon.points}` : ''}</ActionButton>{' '}
					<button type="button" class="small" onClick={() => setConfirming('')}>Cancel</button>
				</> : <button type="button" class="small" disabled={!!mon.problem} onClick={() => setConfirming(mon.id)}>Pick</button>}</td>
			</tr>)}
			{!shown.length && <tr><td class="empty">Nothing matches.</td></tr>}
		</tbody></table></div>
	</div>;
}

function StaffControls({ league, data, act }: { league: DraftLeague, data: PokemonData | null, act: LeagueProps['act'] }) {
	const [player, setPlayer] = useState('');
	const [mon, setMon] = useState('');
	const picker = getPicker(league);
	return <div class="card">
		<h3>Room staff</h3>
		<div class="row">
			<ActionButton disabled={!picker} confirm={picker ? `Skip ${picker.name}? They can make the pick later.` : ''}
				action={() => act('skip')}>Skip {picker?.name || ''}</ActionButton>
			<ActionButton disabled={!league.picks.length} confirm="Take back the last pick (or skip)?" action={() => act('undo')}>Undo the last pick</ActionButton>
			<ActionButton confirm="End the draft and start the season?" action={() => act('end', 'confirm')}>End the draft</ActionButton>
		</div>
		<form class="row" style="margin-top:10px" onSubmit={e => {
			e.preventDefault();
			void act('forcepick', `${player}, ${mon}`);
		}}>
			<select value={player} onChange={e => setPlayer((e.target as HTMLSelectElement).value)} required>
				<option value="">Pick for…</option>
				{league.players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
			</select>
			<input list="draft-available" placeholder="Pokémon" value={mon} onInput={e => setMon((e.target as HTMLInputElement).value)} required />
			<datalist id="draft-available">{getAvailable(league).map(id => <option key={id} value={nameOf(data, id)} />)}</datalist>
			<button type="submit">Make the pick</button>
		</form>
	</div>;
}

export function DraftTab({ view, data, act }: LeagueProps) {
	const { league } = view;
	const me = findYou(league, view.you);
	const picker = league.phase === 'draft' ? getPicker(league) : null;
	const canPick = league.phase === 'draft' && me && me.roster.length < league.settings.picks &&
		(picker?.id === me.id || me.owedPicks > 0);
	return <>
		{league.phase === 'setup' && <div class="notice">
			The draft hasn't started yet.{view.role === 'staff' && <> When the players and pool are ready:{' '}
				<ActionButton class="primary small" confirm="Start the draft now?" action={() => act('start')}>Start the draft</ActionButton></>}
		</div>}
		{league.phase === 'draft' && <div class={`clock ${picker && picker.id === me?.id ? 'yours' : ''}`}>
			<span class="big">{picker ? picker.id === me?.id ? `You're on the clock` : `${picker.name} is on the clock` : 'Every turn is done'}</span>
			{picker && league.pickDeadline && <span>Time left: <strong><Countdown until={league.pickDeadline} /></strong></span>}
			{picker && <span class="muted">Pick {league.pickNumber + 1}</span>}
		</div>}
		{canPick && <PickPanel league={league} data={data} me={me!} act={act} />}
		{league.phase === 'draft' && me && !canPick && me.roster.length < league.settings.picks &&
			<p class="muted">It's not your turn yet: this page updates by itself when it is.</p>}
		{league.phase === 'draft' && view.role === 'staff' && <StaffControls league={league} data={data} act={act} />}
		<div class="card">
			<h2 style="margin-top:0">Board</h2>
			<Board league={league} data={data} youId={view.you} />
		</div>
	</>;
}
